import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { CopyIconButton } from '~/components/common/copy-icon-button';
import { cn } from '~/utils/cn';

import type { RunProgress, RunRead } from '../types/run.api.types';

interface Props {
  runId: string;
  run: RunRead | undefined;
  progress: RunProgress;
  className?: string;
}

/** "a1b2c3d4…9f8e" — keeps both ends of a long id readable; the full value is copied. */
function middleTruncate(id: string): string {
  return id.length > 16 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;
}

/**
 * The batch's ids — batch id and workflow id — muted, middle-truncated and copyable. Shared by the
 * Summary rail (last block of "Batch details") and the Insights rail's Details card.
 */
export function RunIds({ runId, run, progress, className }: Props) {
  const { t } = useTranslation('catalog-enrichment-run');
  // The progress read's workflow id, else the run's own ref (an older run may only carry one).
  const workflowId = progress.workflowId !== '' ? progress.workflowId : (run?.workflowRef ?? null);
  const ids = [
    { key: 'runId', label: t('factsRail.runId'), value: runId },
    { key: 'workflowId', label: t('factsRail.workflowId'), value: workflowId },
  ].filter((i): i is { key: string; label: string; value: string } => !!i.value);
  if (ids.length === 0) return null;
  return (
    <dl
      className={cn(
        'text-muted-foreground grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-0.5 text-xs',
        className,
      )}
    >
      {ids.map((i) => (
        <Fragment key={i.key}>
          <dt className="whitespace-nowrap">{i.label}</dt>
          <dd className="flex min-w-0 items-center justify-end gap-1">
            <span className="font-mono whitespace-nowrap" title={i.value}>
              {middleTruncate(i.value)}
            </span>
            <CopyIconButton text={i.value} label={i.label} className="shrink-0" />
          </dd>
        </Fragment>
      ))}
    </dl>
  );
}
